import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(() => {
      this.#preloadImages();
      this.#styleMenuLinks(); // Apply font, spacing, color, hover
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @type {Object} */
  #state = { activeItem: null };

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]') || null;
  }

  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  // ------------------------------
  // Activation / deactivation
  // ------------------------------

  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
    this.#debouncedActivateHandler(event);
  };

  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const prevItem = this.#state.activeItem;
    if (prevItem) prevItem.ariaExpanded = 'false';

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) submenu = this.overflowMenu;

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  deactivate(event) {
    this.#debouncedActivateHandler.cancel();
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (item === this.#state.activeItem) this.#debouncedDeactivate();
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => item.removeAttribute('data-animating'), Math.max(0, this.animationDelay - 150));
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  // ------------------------------
  // Preload images
  // ------------------------------

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((img) => img.removeAttribute('loading'));
  };

  // ------------------------------
  // Parent & Child link styling + hover behavior
  // ------------------------------

  #styleMenuLinks = () => {
    // Parent links
    const parentLinks = this.querySelectorAll('ul.mega-menu__list > li > div > a.mega-menu__link');
    parentLinks.forEach((link) => {
      const titleSpan = link.querySelector('.mega-menu__link-title');
      if (!titleSpan) return;

      // Parent styling
      titleSpan.style.fontSize = '17px';
      titleSpan.style.fontWeight = '400';
      titleSpan.style.padding = '4px 0 4px 16px'; // same as children
      titleSpan.style.lineHeight = '1.4';
      titleSpan.style.color = '#ffffff';         // default color

      // Hover effect same as children
      link.addEventListener('mouseenter', () => {
        link.classList.add('mega-menu__link--hover');
        titleSpan.style.color = ''; // children hover green via CSS
      });
      link.addEventListener('mouseleave', () => {
        link.classList.remove('mega-menu__link--hover');
        titleSpan.style.color = '#ffffff'; // revert to white
      });
    });

    // Child links
    const childLinks = this.querySelectorAll('ul.mega-menu__list li ul li a.mega-menu__link');
    childLinks.forEach((link) => {
      const titleSpan = link.querySelector('.mega-menu__link-title');
      if (!titleSpan) return;

      titleSpan.style.fontSize = '14px';
      titleSpan.style.fontWeight = '400';
      titleSpan.style.padding = '4px 0 4px 16px';
      titleSpan.style.lineHeight = '1.4';
      titleSpan.style.color = ''; // default color
      // Hover handled by existing CSS class
    });
  };
}

// ------------------------------
// Register custom element
// ------------------------------

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

// ------------------------------
// Helper functions
// ------------------------------

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element.matches('[slot="more"]')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }
  return element.querySelector('[ref="menuitem"]') || element;
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
